import { useTranslation } from "react-i18next";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import {
  SUBSCRIPTION_QUERY_MAX_LENGTH,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_TIER_FILTER_MAX_LENGTH,
  type SubscriptionStatus,
  type SubscriptionTier,
} from "./constants";

interface SubscriptionsFiltersProps {
  readonly query: string;
  readonly status: SubscriptionStatus | null;
  readonly tier: SubscriptionTier | null;
  readonly onQueryChange: (query: string) => void;
  readonly onStatusChange: (status: SubscriptionStatus | null) => void;
  readonly onTierChange: (tier: SubscriptionTier | null) => void;
  readonly onReset: () => void;
}

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
const LABEL_CLASS = "font-sans text-sm font-medium text-foreground";

export function SubscriptionsFilters({
  query,
  status,
  tier,
  onQueryChange,
  onStatusChange,
  onTierChange,
  onReset,
}: SubscriptionsFiltersProps) {
  const { t } = useTranslation();
  const hasFilters = query.trim().length > 0 || status !== null || tier !== null;

  return (
    <div data-testid="subscriptions-filters" className="flex flex-wrap items-end gap-3">
      <div className="min-w-[16rem] flex-1 space-y-1.5">
        <label htmlFor="subscriptions-search" className={LABEL_CLASS}>
          {t("subscriptions.list.filters.search")}
        </label>
        <Input
          id="subscriptions-search"
          data-testid="subscriptions-search-input"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          maxLength={SUBSCRIPTION_QUERY_MAX_LENGTH}
          placeholder={t("subscriptions.list.filters.searchPlaceholder")}
          autoComplete="off"
        />
      </div>

      <div className="space-y-1.5">
        <label htmlFor="subscriptions-status" className={LABEL_CLASS}>
          {t("subscriptions.list.filters.status")}
        </label>
        <select
          id="subscriptions-status"
          data-testid="filter-status"
          value={status ?? ""}
          onChange={(e) =>
            onStatusChange(e.target.value === "" ? null : (e.target.value as SubscriptionStatus))
          }
          className={SELECT_CLASS}
        >
          <option value="">{t("subscriptions.list.filters.statusAll")}</option>
          {SUBSCRIPTION_STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`subscriptions.list.status.${value}`)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="subscriptions-tier" className={LABEL_CLASS}>
          {t("subscriptions.list.filters.tier")}
        </label>
        {/*
          Capacity pivot: tier is a free-form key (e.g. `TIER_1`), no longer a
          closed enum, so the filter is a text input. A dropdown sourced from
          the tiers config can replace this once it's wired here.
        */}
        <Input
          id="subscriptions-tier"
          data-testid="filter-tier"
          value={tier ?? ""}
          onChange={(e) => {
            // Trim-on-change: tier keys are structured (e.g. `TIER_1`), never
            // have leading/trailing spaces, so a whitespace-only value collapses
            // to null immediately rather than deferring to the query builder.
            const trimmed = e.target.value.trim();
            onTierChange(trimmed === "" ? null : trimmed);
          }}
          maxLength={SUBSCRIPTION_TIER_FILTER_MAX_LENGTH}
          placeholder={t("subscriptions.list.filters.tierPlaceholder")}
          autoComplete="off"
        />
      </div>

      {hasFilters && (
        <Button data-testid="subscriptions-filters-reset" variant="outline" onClick={onReset}>
          {t("subscriptions.list.filters.reset")}
        </Button>
      )}
    </div>
  );
}
